import { useEffect, useState } from "react";
import {
  FiCheck, FiAlertCircle, FiTrendingUp, FiX,
  FiExternalLink, FiLock, FiShield
} from "react-icons/fi";
import axios from "../api/axios";

/* ═══════════════════════════════════════════
   STYLES - Matching AdminDashboard renderShares design
═══════════════════════════════════════════════ */
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

.buyshare-root {
  font-family: 'Montserrat', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 6px 5px;
}
@media (min-width: 640px) {
  .buyshare-root {
    padding: 24px 24px;
  }
}

.buyshare-container {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Hero Section */
.buyshare-hero {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 9px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
@media (min-width: 768px) {
  .buyshare-hero {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.buyshare-hero-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.buyshare-hero-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fbbf24;
  animation: pulse 2s infinite;
  box-shadow: 0 0 8px rgba(251, 191, 36, 0.6);
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}
.buyshare-hero-badge-text {
  font-size: 11px;
  font-weight: 800;
  color: #fde68a;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.buyshare-hero-title {
  font-size: 24px;
  font-weight: 900;
  color: white;
  line-height: 1.2;
  margin-bottom: 4px;
}
@media (min-width: 768px) {
  .buyshare-hero-title {
    font-size: 30px;
  }
}
.buyshare-hero-sub {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.buyshare-wallet-chip {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
}
.buyshare-wallet-label {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  margin-bottom: 6px;
}
.buyshare-wallet-amount {
  font-size: 24px;
  font-weight: 900;
  color: #fbbf24;
}

/* Alerts */
.buyshare-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}
.buyshare-alert.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}
.buyshare-alert.success {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #059669;
}

/* Section Header */
.buyshare-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.buyshare-section-title {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
}

/* Plans Grid */
.buyshare-plans-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 640px) {
  .buyshare-plans-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1280px) {
  .buyshare-plans-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Plan Card - Matching AdminDashboard renderShares */
.buyshare-plan-card {
  background: white;
  border-radius: 9px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}
.buyshare-plan-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.buyshare-plan-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.buyshare-plan-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
}
.buyshare-plan-image-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #dbeafe 0%, #e9d5ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}
.buyshare-plan-info {
  flex: 1;
}
.buyshare-plan-name {
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 2px;
}
.buyshare-plan-price {
  font-size: 14px;
  color: red;
  font-weight: 600;
}

.buyshare-plan-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}
.buyshare-plan-stat {
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
}
.buyshare-plan-stat.slate {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}
.buyshare-plan-stat.blue {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}
.buyshare-plan-stat.green {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
}
.buyshare-plan-stat.emerald {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.buyshare-plan-stat-label {
  color: #64748b;
  font-weight: 500;
  margin-right: 4px;
}
.buyshare-plan-stat-label.blue { color: #2563eb; }
.buyshare-plan-stat-label.green { color: #059669; }
.buyshare-plan-stat-label.emerald { color: #047857; }
.buyshare-plan-stat-value {
  font-weight: 800;
}
.buyshare-plan-stat-value.blue { color: #1d4ed8; }
.buyshare-plan-stat-value.green { color: #047857; }
.buyshare-plan-stat-value.emerald { color: #065f46; }

.buyshare-plan-weekly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}
.buyshare-plan-weekly-label {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
}
.buyshare-plan-weekly-value {
  font-size: 14px;
  font-weight: 900;
  color: #78350f;
}

.buyshare-plan-progress {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}
.buyshare-plan-progress-bar {
  background: #e2e8f0;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}
.buyshare-plan-progress-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%);
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}
.buyshare-plan-progress-fill.soldout {
  background: #ef4444;
}
.buyshare-plan-progress-text {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
}

.buyshare-plan-status {
  position: absolute;
  top: 12px;
  right: 12px;
}
.buyshare-plan-status-badge {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}
.buyshare-plan-status-badge.open {
  background: #dcfce7;
  color: #15803d;
}
.buyshare-plan-status-badge.closed {
  background: #fed7aa;
  color: #c2410c;
}
.buyshare-plan-status-badge.soldout {
  background: #fee2e2;
  color: #b91c1c;
}

.buyshare-plan-btn {
  width: 100%;
  padding: 8px 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 800;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}
.buyshare-plan-btn.active {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
}
.buyshare-plan-btn.active:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%);
}
.buyshare-plan-btn.disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

/* Empty State */
.buyshare-empty {
  text-align: center;
  padding: 48px 32px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.buyshare-empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.7;
}
.buyshare-empty-text {
  font-size: 16px;
  font-weight: 700;
  color: #64748b;
}

/* Loading Skeleton */
.buyshare-skeleton {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.buyshare-skeleton-hero {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 16px;
  padding: 24px;
  animation: pulse 1.5s infinite;
}
.buyshare-skeleton-hero-line {
  background: rgba(255,255,255,0.3);
  border-radius: 4px;
}
.buyshare-skeleton-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 640px) {
  .buyshare-skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1280px) {
  .buyshare-skeleton-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.buyshare-skeleton-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 16px;
  animation: pulse 1.5s infinite;
}
.buyshare-skeleton-line {
  background: #e2e8f0;
  border-radius: 4px;
  height: 8px;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Modal Overlay */
.buyshare-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
}
.buyshare-modal {
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 30px -8px rgba(0, 0, 0, 0.25);
}
.buyshare-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}
.buyshare-modal-title {
  font-size: 15px;
  font-weight: 800;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.buyshare-modal-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.buyshare-modal-close:hover {
  background: #fee2e2;
  color: #dc2626;
}
.buyshare-modal-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Modal Plan Box */
.buyshare-modal-plan-box {
  background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 14px;
}
.buyshare-modal-plan-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.buyshare-modal-plan-image {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}
.buyshare-modal-plan-image-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}
.buyshare-modal-plan-info {
  flex: 1;
}
.buyshare-modal-plan-name {
  font-size: 15px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 2px;
}
.buyshare-modal-plan-desc {
  font-size: 11px;
  color: #64748b;
}
.buyshare-modal-plan-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.buyshare-modal-plan-stat {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  text-align: center;
}
.buyshare-modal-plan-stat-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.buyshare-modal-plan-stat-value {
  font-size: 14px;
  font-weight: 800;
}
.buyshare-modal-plan-stat-value.slate { color: #1e293b; }
.buyshare-modal-plan-stat-value.blue { color: #2563eb; }
.buyshare-modal-plan-stat-value.purple { color: #7c3aed; }
.buyshare-modal-plan-stat-value.green { color: #059669; }
.buyshare-modal-plan-weekly {
  grid-column: span 2;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 10px;
  text-align: center;
  margin-top: 8px;
}
.buyshare-modal-plan-weekly-label {
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.buyshare-modal-plan-weekly-value {
  font-size: 16px;
  font-weight: 800;
  color: #78350f;
}

/* Locked Warning */
.buyshare-locked-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 12px;
}
.buyshare-locked-warning-title {
  font-size: 12px;
  font-weight: 800;
  color: #c2410c;
  margin-bottom: 2px;
}
.buyshare-locked-warning-text {
  font-size: 11px;
  font-weight: 600;
  color: #ea580c;
}

/* Wallet Selector */
.buyshare-wallet-selector-label {
  font-size: 12px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 6px;
  display: block;
}
.buyshare-wallet-buttons {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}
@media (min-width: 640px) {
  .buyshare-wallet-buttons {
    grid-template-columns: repeat(3, 1fr);
  }
}
.buyshare-wallet-btn {
  padding: 10px;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-family: 'Montserrat', sans-serif;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}
.buyshare-wallet-btn:hover {
  border-color: #93c5fd;
}
.buyshare-wallet-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1d4ed8;
}
.buyshare-wallet-btn-name {
  font-size: 12px;
  font-weight: 800;
}
.buyshare-wallet-btn-balance {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  margin-top: 3px;
}
.buyshare-wallet-btn.active .buyshare-wallet-btn-balance {
  color: #1d4ed8;
}

/* Total Row */
.buyshare-total-row {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.buyshare-total-label {
  font-size: 12px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.9);
}
.buyshare-total-value {
  font-size: 18px;
  font-weight: 800;
  color: #fbbf24;
}

/* Balance Row */
.buyshare-balance-row {
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.buyshare-balance-row.ok {
  background: #ecfdf5;
  border-color: #bbf7d0;
}
.buyshare-balance-row.low {
  background: #fef2f2;
  border-color: #fecaca;
}
.buyshare-balance-label {
  font-size: 12px;
  font-weight: 800;
  color: #1e293b;
}
.buyshare-balance-value {
  font-size: 15px;
  font-weight: 800;
}
.buyshare-balance-row.ok .buyshare-balance-value {
  color: #059669;
}
.buyshare-balance-row.low .buyshare-balance-value {
  color: #dc2626;
}
.buyshare-shortfall {
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  color: #dc2626;
  padding: 4px 0;
}

/* Income Box */
.buyshare-income-box {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 4px 4px;
}
.buyshare-income-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.buyshare-income-title {
  font-size: 11px;
  font-weight: 800;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.buyshare-income-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.buyshare-income-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #065f46;
}
.buyshare-income-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  flex-shrink: 0;
  margin-top: 4px;
}

/* Terms Box */
.buyshare-terms-box {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 4px 8px;
}
.buyshare-terms-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.buyshare-terms-title {
  font-size: 8px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.buyshare-terms-preview {
  background: white;
  border: 1px solid #fde68a;
  border-radius: 6px;
  // padding: 10px;
  // margin-bottom: 10px;
}
.buyshare-terms-preview-text {
  font-size: 11px;
  color: #78350f;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.buyshare-terms-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 800;
  color: #2563eb;
  text-decoration: none;
  // margin-bottom: 10px;
  cursor: pointer;
  transition: color 0.2s ease;
}
.buyshare-terms-link:hover {
  color: #1d4ed8;
}
.buyshare-terms-checkbox-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}
.buyshare-terms-checkbox {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: #1e3a5f;
  cursor: pointer;
}
.buyshare-terms-checkbox-label {
  font-size: 10px;
  font-weight: 600;
  color: #334155;
  line-height: 1.5;
}

/* Buy Button */
.buyshare-buy-btn {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  margin-bottom: 40px;
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 12px -4px rgba(0, 0, 0, 0.1), 0 3px 4px -2px rgba(0, 0, 0, 0.05);
}
.buyshare-buy-btn.ready {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
}
.buyshare-buy-btn.ready:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%);
}
.buyshare-buy-btn.disabled {
  background: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}
.buyshare-buy-btn.locked {
  background: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}
.buyshare-buy-btn.insufficient {
  background: #ef4444;
  box-shadow: none;
  cursor: not-allowed;
}

/* Terms Modal */
.buyshare-terms-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}
.buyshare-terms-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.25);
}
.buyshare-terms-header {
  // display: flex;
  // align-items: center;
  // justify-content: space-between;
  // padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}
.buyshare-terms-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.buyshare-terms-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}
.buyshare-terms-title {
  font-size: 10px;
  font-weight: 700;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.buyshare-terms-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.buyshare-terms-close:hover {
  background: #fee2e2;
  color: #dc2626;
}
.buyshare-terms-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.buyshare-terms-section {
  padding: 16px;
  border-radius: 12px;
}
.buyshare-terms-section.blue {
  background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
  border: 1px solid #dbeafe;
}
.buyshare-terms-section.orange {
  background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%);
  border: 1px solid #fed7aa;
}
.buyshare-terms-section.green {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #bbf7d0;
}
.buyshare-terms-section-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.buyshare-terms-section-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.buyshare-terms-section-icon.blue {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}
.buyshare-terms-section-icon.orange {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #ea580c;
}
.buyshare-terms-section-icon.green {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #059669;
}
.buyshare-terms-section-title {
  font-size: 14px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 4px;
}
.buyshare-terms-section-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.5;
}
.buyshare-terms-list-inline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.buyshare-terms-item-inline {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #f1f5f9;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  line-height: 1.6;
}
.buyshare-terms-bullet {
  color: #2563eb;
  font-weight: 900;
  font-size: 14px;
  flex-shrink: 0;
}
.buyshare-terms-numbered-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.buyshare-terms-numbered-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #f59e0b;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  line-height: 1.6;
}
.buyshare-terms-number {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-weight: 900;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
const BuyShare = () => {
  const [plans, setPlans] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState("lbbp");

  const fetchData = async () => {
    try {
      const [plansRes, walletRes] = await Promise.all([
          axios.get("/share/plans"),
          axios.get("/share/dashboard-summary"),
        ]);
        setPlans((plansRes.data || []).filter(p => p.isActive && !p.isExhausted));
      setWallet(walletRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openModal = (plan) => {
    setSelectedPlan(plan);
    setTermsAccepted(false);
    setErrorMsg("");
    setSuccessMsg("");
    setSelectedWallet("lbbp");
  };
  const closeModal = () => {
    setSelectedPlan(null); setTermsAccepted(false); setErrorMsg("");
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;
    if (!termsAccepted) { setErrorMsg("Please accept the Terms & Conditions before proceeding."); return; }
    const price = selectedPlan.price || selectedPlan.amount;
    
    let walletBalance = 0;
    let walletName = "";
    
    if (selectedWallet === "lbbp") {
      walletBalance = wallet.lbbpWallet || wallet.walletBalance || 0;
      walletName = "LBBP";
    } else if (selectedWallet === "puap") {
      walletBalance = wallet.puapWallet || 0;
      walletName = "PU & AP";
    } else if (selectedWallet === "ewallet") {
      walletBalance = wallet.eWalletBalance || wallet.eWallet || 0;
      walletName = "E-Wallet";
    }
    
    if (price > walletBalance) {
      setErrorMsg(`Insufficient ${walletName} wallet balance. Need ₹${price.toLocaleString()} but have ₹${walletBalance.toLocaleString()}`);
      return;
    }
    
    try {
      setPurchasing(true); setErrorMsg("");
      await axios.post("/share/buy", {
        planId: selectedPlan._id,
        walletType: selectedWallet,
      });
      setSuccessMsg("Plan purchased successfully!");
      setTermsAccepted(false);
      setTimeout(() => { fetchData(); setSuccessMsg(""); setSelectedPlan(null); }, 2200);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Purchase failed. Try again.");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) return (
    <div className="buyshare-root">
      <style>{styles}</style>
      <div className="buyshare-container">
        <div className="buyshare-skeleton">
          <div className="buyshare-skeleton-hero">
            <div className="buyshare-skeleton-hero-line" style={{height: '16px', width: '100px', marginBottom: '8px'}}></div>
            <div className="buyshare-skeleton-hero-line" style={{height: '32px', width: '280px', marginBottom: '4px'}}></div>
            <div className="buyshare-skeleton-hero-line" style={{height: '16px', width: '200px'}}></div>
          </div>
          <div className="buyshare-skeleton-grid">
            {[1,2,3].map(i => (
              <div key={i} className="buyshare-skeleton-card">
                <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px'}}>
                  <div className="buyshare-skeleton-line" style={{width: '48px', height: '48px', borderRadius: '8px'}}></div>
                  <div style={{flex: 1}}>
                    <div className="buyshare-skeleton-line" style={{width: '120px', height: '16px', marginBottom: '8px'}}></div>
                    <div className="buyshare-skeleton-line" style={{width: '80px', height: '12px'}}></div>
                  </div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px'}}>
                  <div className="buyshare-skeleton-line" style={{height: '40px', borderRadius: '6px'}}></div>
                  <div className="buyshare-skeleton-line" style={{height: '40px', borderRadius: '6px'}}></div>
                  <div className="buyshare-skeleton-line" style={{height: '40px', borderRadius: '6px'}}></div>
                  <div className="buyshare-skeleton-line" style={{height: '40px', borderRadius: '6px'}}></div>
                </div>
                <div className="buyshare-skeleton-line" style={{width: '80px', height: '24px', borderRadius: '999px', marginBottom: '12px'}}></div>
                <div className="buyshare-skeleton-line" style={{height: '40px', borderRadius: '8px'}}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="buyshare-root">
      <style>{styles}</style>
      <div className="buyshare-container">

        {/* Hero + Wallet */}
        <div className="buyshare-hero">
          <div>
            <div className="buyshare-hero-badge">
              <div className="buyshare-hero-dot"></div>
              <span className="buyshare-hero-badge-text">GCP Investment</span>
            </div>
            <h1 className="buyshare-hero-title">Growth Capital Partner</h1>
            <p className="buyshare-hero-sub">Get loan and earn weekly income</p>
          </div>
          {wallet && (
            <div className="buyshare-wallet-chip">
              <div className="buyshare-wallet-label">Available LBBP Balance</div>
              <div className="buyshare-wallet-amount">₹{(wallet.lbbpWallet || wallet.walletBalance || 0).toLocaleString("en-IN")}</div>
            </div>
          )}
        </div>

        {/* Alerts */}
        {errorMsg && !selectedPlan && (
          <div className="buyshare-alert error">
            <FiAlertCircle size={20} />
            <span>{errorMsg}</span>
          </div>
        )}
        {successMsg && !selectedPlan && (
          <div className="buyshare-alert success">
            <FiCheck size={20} />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Plans Section */}
        <div>
          <div className="buyshare-section-header">
            <h3 className="buyshare-section-title">Available Plans</h3>
          </div>
          {plans.length === 0 ? (
            <div className="buyshare-empty">
              <div className="buyshare-empty-icon">📋</div>
              <div className="buyshare-empty-text">No plans available at the moment</div>
            </div>
          ) : (
            <div className="buyshare-plans-grid">
              {plans.map(plan => {
                const purchaseCount = Number(plan.purchasedShares || 0);
                const totalShares = Number(plan.totalShares || 0);
                const availableShares = totalShares > 0 ? Math.max(0, totalShares - purchaseCount) : Infinity;
                const isSoldOut = availableShares === 0 || plan.isExhausted;
                const locked = plan.isPurchaseEnabled === false || isSoldOut;
                const price = plan.price || plan.amount;
                const weeks = plan.cycleWeeks || Math.ceil((plan.cycleDays || 0) / 7);
                const total = plan.totalReturn || (price + (plan.roiAmount || 0));
                const getStatusClass = () => {
                  if (isSoldOut) return "soldout";
                  if (locked) return "closed";
                  return "open";
                };
                const getStatusText = () => {
                  if (isSoldOut) return "🔴 Sold Out";
                  if (locked) return "🔒 New Plan Launch";
                  return "🟢 Active";
                };
                return (
                  <div
                    key={plan._id}
                    className="buyshare-plan-card"
                    onClick={() => !isSoldOut && openModal(plan)}
                  >
                    {/* Status Badge at Top Right */}
                    <div className="buyshare-plan-status">
                      <span className={`buyshare-plan-status-badge ${getStatusClass()}`}>{getStatusText()}</span>
                    </div>

                    <div className="buyshare-plan-header">
                      {plan.featuredImage ? (
                        <img 
                          src={plan.featuredImage} 
                          alt={plan.name} 
                          className="buyshare-plan-image"
                        />
                        
                      ) : (
                        <div className="buyshare-plan-image-placeholder">📈</div>
                      )}
                      <div className="buyshare-plan-info">
                        <div className="buyshare-plan-name">{plan.name}</div>
                        <div className="buyshare-plan-price">₹{price?.toLocaleString("en-IN")}</div>
                        {/* {purchaseCount > 0 && (
                          <div className="text-xs text-blue-600 font-semibold">
                            Purchased {purchaseCount} time{purchaseCount !== 1 ? 's' : ''}
                          </div>
                        )}
                        {totalShares > 0 && (
                          <div className="text-xs text-slate-600 font-semibold mt-1">
                            {isSoldOut ? (
                              <span className="text-red-600">Sold Out ({totalShares} total)</span>
                            ) : (
                              `Available: ${availableShares} / ${totalShares} total`
                            )}
                          </div>
                        )} */}
                      </div>
                    </div>

                    {totalShares > 0 && (
                      <div className="buyshare-plan-progress">
                        <div className="buyshare-plan-progress-bar">
                          <div
                            className={`buyshare-plan-progress-fill ${isSoldOut ? 'soldout' : ''}`}
                            style={{ width: `${Math.min(100, (purchaseCount / totalShares) * 100)}%` }}
                          />
                        </div>
                        {/* <div className="buyshare-plan-progress-text">
                          <span>{purchaseCount} sold</span>
                          <span>{totalShares} total</span>
                        </div> */}
                      </div>
                    )}

                    <div className="buyshare-plan-stats">
                      <div className="buyshare-plan-stat slate">
                        <span className="buyshare-plan-stat-label">Cycle:</span>
                        <span className="buyshare-plan-stat-value">{weeks} wks</span>
                      </div>
                      <div className="buyshare-plan-stat blue">
                        <span className="buyshare-plan-stat-label blue">Interest:</span>
                        <span className="buyshare-plan-stat-value blue">{plan.roiPercentage || 0}%</span>
                      </div>
                      <div className="buyshare-plan-stat green">
                        <span className="buyshare-plan-stat-label green">Profit:</span>
                        <span className="buyshare-plan-stat-value green">₹{Number(plan.roiAmount).toFixed(2)}</span>
                      </div>
                      <div className="buyshare-plan-stat emerald">
                        <span className="buyshare-plan-stat-label emerald">Total:</span>
                        <span className="buyshare-plan-stat-value emerald">₹{Number(total).toFixed(2)}</span>
                      </div>
                    </div>

                    {plan.weeklyPayout && (
                      <div className="buyshare-plan-weekly">
                        <span className="buyshare-plan-weekly-label">Weekly EMI</span>
                        <span className="buyshare-plan-weekly-value">₹{Number(plan.weeklyPayout).toLocaleString("en-IN")}</span>
                      </div>
                    )}

                    <button
                      disabled={locked || isSoldOut}
                      className={`buyshare-plan-btn ${(locked || isSoldOut) ? 'disabled' : 'active'}`}
                    >
                      {isSoldOut ? "Sold Out" : locked ? "New Plan Launch" : "Start Growth"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      {selectedPlan && (() => {
        const plan = selectedPlan;
        const locked = plan.isPurchaseEnabled === false;
        const price = plan.price || plan.amount;
        const weeks = plan.cycleWeeks || Math.ceil((plan.cycleDays || 0) / 7);
        const total = plan.totalReturn || (price + (plan.roiAmount || 0));
        const hasFunds = wallet && (
          (selectedWallet === "lbbp" && (wallet.lbbpWallet || wallet.walletBalance || 0) >= price) ||
          (selectedWallet === "puap" && (wallet.puapWallet || 0) >= price) ||
          (selectedWallet === "ewallet" && (wallet.eWalletBalance || wallet.eWallet || 0) >= price)
        );

        const btnClass = locked ? "locked"
          : purchasing ? "disabled"
          : !termsAccepted ? "disabled"
          : !hasFunds ? "insufficient"
          : "ready";

        const btnText = locked ? "🔒 Purchase Closed by Admin"
          : purchasing ? "Processing…"
          : !termsAccepted ? "Accept Terms to Continue"
          : !hasFunds ? `Insufficient Balance`
          : `I Agree & Buy Now — ₹${price?.toLocaleString("en-IN")}`;

        return (
          <div className="buyshare-modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
            <div className="buyshare-modal">
              <div className="buyshare-modal-header">
                <h3 className="buyshare-modal-title">Purchase Summary</h3>
                <button className="buyshare-modal-close" onClick={closeModal}><FiX size={20} /></button>
              </div>

              <div className="buyshare-modal-body">
                {successMsg && (
                  <div className="buyshare-alert success">
                    <FiCheck size={18} />
                    <span>{successMsg}</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="buyshare-alert error">
                    <FiAlertCircle size={18} />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <div className="buyshare-modal-plan-box">
                  <div className="buyshare-modal-plan-header">
                    {plan.featuredImage ? (
                      <img src={plan.featuredImage} alt={plan.name} className="buyshare-modal-plan-image" />
                    ) : (
                      <div className="buyshare-modal-plan-image-placeholder">📈</div>
                    )}
                    <div className="buyshare-modal-plan-info">
                      <div className="buyshare-modal-plan-name">{plan.name}</div>
                      {plan.description && <div className="buyshare-modal-plan-desc">{plan.description}</div>}
                    </div>
                  </div>
                  <div className="buyshare-modal-plan-stats">
                    <div className="buyshare-modal-plan-stat">
                      <div className="buyshare-modal-plan-stat-label">Loan Pay</div>
                      <div className="buyshare-modal-plan-stat-value slate">₹{price?.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="buyshare-modal-plan-stat">
                      <div className="buyshare-modal-plan-stat-label">Profit</div>
                      <div className="buyshare-modal-plan-stat-value blue">₹{(plan.roiAmount || 0).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="buyshare-modal-plan-stat">
                      <div className="buyshare-modal-plan-stat-label">Cycle</div>
                      <div className="buyshare-modal-plan-stat-value purple">{weeks} Weeks</div>
                    </div>
                    <div className="buyshare-modal-plan-stat">
                      <div className="buyshare-modal-plan-stat-label">Total Return</div>
                      <div className="buyshare-modal-plan-stat-value green">₹{total?.toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                  {plan.weeklyPayout && (
                    <div className="buyshare-modal-plan-weekly">
                      <div className="buyshare-modal-plan-weekly-label">Weekly Payout</div>
                      <div className="buyshare-modal-plan-weekly-value">₹{Number(plan.weeklyPayout).toLocaleString("en-IN")}</div>
                    </div>
                  )}
                </div>

                {locked && (
                  <div className="buyshare-locked-warning">
                    <FiAlertCircle size={20} style={{color: '#c2410c', flexShrink: 0, marginTop: '2px'}} />
                    <div>
                      <div className="buyshare-locked-warning-title">Purchases Currently Closed</div>
                      <div className="buyshare-locked-warning-text">Admin has temporarily disabled new purchases for this plan.</div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="buyshare-wallet-selector-label">Select Wallet</label>
                  <div className="buyshare-wallet-buttons">
                    {[
                      { value: 'lbbp', label: 'LBBP Wallet', balance: wallet?.lbbpWallet || wallet?.walletBalance || 0 },
                      { value: 'puap', label: 'PU & AP Wallet', balance: wallet?.puapWallet || 0 },
                      { value: 'ewallet', label: 'E-Wallet', balance: wallet?.eWalletBalance || wallet?.eWallet || 0 }
                    ].map(walletOption => (
                      <button
                        key={walletOption.value}
                        type="button"
                        onClick={() => setSelectedWallet(walletOption.value)}
                        className={`buyshare-wallet-btn ${selectedWallet === walletOption.value ? 'active' : ''}`}
                      >
                        <div className="buyshare-wallet-btn-name">{walletOption.label}</div>
                        <div className="buyshare-wallet-btn-balance">₹{walletOption.balance.toLocaleString("en-IN")}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="buyshare-total-row">
                  <span className="buyshare-total-label">Total Loan Pay</span>
                  <span className="buyshare-total-value">₹{price?.toLocaleString("en-IN")}</span>
                </div>
                {wallet && (
                  <div className={`buyshare-balance-row ${hasFunds ? 'ok' : 'low'}`}>
                    <span className="buyshare-balance-label">
                      {selectedWallet === "lbbp" ? "LBBP Wallet" : 
                       selectedWallet === "puap" ? "PU & AP Wallet" : "E-Wallet"} Balance
                    </span>
                    <span className="buyshare-balance-value">
                      ₹{selectedWallet === "lbbp" ? (wallet.lbbpWallet || wallet.walletBalance || 0).toLocaleString("en-IN") :
                         selectedWallet === "puap" ? (wallet.puapWallet || 0).toLocaleString("en-IN") :
                         (wallet.eWalletBalance || wallet.eWallet || 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
                {wallet && !hasFunds && (
                  <div className="buyshare-shortfall">
                    Need ₹{(price - (selectedWallet === "lbbp" ? (wallet.lbbpWallet || wallet.walletBalance || 0) :
                                    selectedWallet === "puap" ? (wallet.puapWallet || 0) :
                                    (wallet.eWalletBalance || wallet.eWallet || 0))).toLocaleString("en-IN")} more
                  </div>
                )}

                <div className="buyshare-income-box">
                  <div className="buyshare-income-header">
                    <FiTrendingUp size={16} style={{color: '#059669'}} />
                    <span className="buyshare-income-title">Income Breakdown</span>
                  </div>
                  <div className="buyshare-income-list">
                    {["Level Income: 5%, 3%, 2% across levels", "Share income weekly EMI payments", "Royalty: 3–5% (requires 30+ active team)"].map((t, i) => (
                      <div key={i} className="buyshare-income-item">
                        <div className="buyshare-income-dot"></div>
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="buyshare-terms-box">
                  <div className="buyshare-terms-header">
                    <FiShield size={16} style={{color: '#92400e'}} />
                    <span className="buyshare-terms-title">Terms & Conditions</span>
                  </div>
                  <div className="buyshare-terms-preview">
                    <div className="buyshare-terms-preview-text">
                      <span style={{fontSize: '20px'}}>🔒</span>
                      <span>This is a <strong>Private Business Loan</strong>, not an investment. No ownership rights or guaranteed returns.</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setShowTermsModal(true)} 
                    className="buyshare-terms-link"
                  >
                    <FiExternalLink size={14} /> Read Full Terms & Conditions
                  </button>
                  <label className="buyshare-terms-checkbox-wrap">
                    <input
                      type="checkbox"
                      className="buyshare-terms-checkbox"
                      checked={termsAccepted}
                      onChange={e => { setTermsAccepted(e.target.checked); if (e.target.checked) setErrorMsg(""); }}
                    />
                    <span className="buyshare-terms-checkbox-label">
                      I have read and agree to the Terms & Conditions. I understand this is a <strong>Private Business Loan</strong> and not an investment.
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleBuy}
                  disabled={locked || purchasing || !termsAccepted || !hasFunds}
                  className={`buyshare-buy-btn ${btnClass}`}
                >
                  {btnText}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="buyshare-terms-overlay" onClick={e => e.target === e.currentTarget && setShowTermsModal(false)}>
          <div className="buyshare-terms-modal">
            <div className="buyshare-terms-header">
              <div className="buyshare-terms-header-left">
                <div className="buyshare-terms-icon">🔒</div>
                <h3 className="buyshare-terms-title">Legal Disclaimer</h3>
              </div>
              <button className="buyshare-terms-close" onClick={() => setShowTermsModal(false)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="buyshare-terms-body">
              <div className="buyshare-terms-section blue">
                <div className="buyshare-terms-section-header">
                  <div className="buyshare-terms-section-icon blue">📜</div>
                  <div>
                    <div className="buyshare-terms-section-title">Legal Disclaimer</div>
                    <div className="buyshare-terms-section-subtitle">This platform does not operate any kind of Public Deposit Scheme, nor does it offer any form of Investment Scheme.</div>
                  </div>
                </div>
                <div className="buyshare-terms-list-inline">
                  {[
                    "This platform does not operate any kind of Public Deposit Scheme, nor does it offer any form of Investment Scheme.",
                    "This facility is available only to a limited number of selected and invited individuals under a Private Loan Agreement, and is strictly private in nature.",
                    "The Company does not promise, advertise, or guarantee any fixed return, assured profit, guaranteed income, or fixed interest rate of any kind.",
                    "The Lender explicitly acknowledges and agrees that amount provided is not an investment, is not a deposit, is not a Fixed Deposit (FD), is not a chit fund, CIS, Ponzi scheme, or any collective investment scheme.",
                    "This amount is provided solely as an Unsecured Private Business Loan to Company for its business operations."
                  ].map((term, i) => (
                    <div key={i} className="buyshare-terms-item-inline">
                      <span className="buyshare-terms-bullet">•</span>
                      <span>{term}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="buyshare-terms-section orange">
                <div className="buyshare-terms-section-header">
                  <div className="buyshare-terms-section-icon orange">⚠️</div>
                  <div>
                    <div className="buyshare-terms-section-title">Important Terms</div>
                  </div>
                </div>
                <div className="buyshare-terms-numbered-list">
                  {[
                    "This facility is strictly invitation-based and not open to general public.",
                    "The Lender provides loan voluntarily, based on their own independent decision, without any pressure, inducement, or misrepresentation.",
                    "This is a completely unsecured loan, and no guarantee or security is provided by Company.",
                    "In consideration of this loan, Lender shall not receive: Any ownership in Company, Any shareholding, Any voting rights, Any management or control rights.",
                    "This loan is not related to any MLM scheme, referral income, network income, or bonus structure.",
                    "Repayment shall depend on Company's cash flow and business conditions, and Company may: Reschedule payment dates, Make partial payments, if required.",
                    "The repayment (EMI) of this loan shall be made on a weekly basis. In case of any technical, administrative, or business-related issues, Company reserves right to extend or reschedule payment date.",
                    "TDS / Tax Deduction may be applicable as per prevailing tax laws.",
                    "This loan is subject to business risks, and Company shall not be held fully liable for any losses, delays, or uncertainties arising from business operations.",
                    "The Lender may request pre-closure or exit, which shall be subject to Company's policies and financial position, and may be accepted or declined accordingly.",
                    "In case of default or force majeure events (such as natural disasters, technical failures, government restrictions, etc.), Company shall have right to delay or reschedule payments.",
                    "The Company reserves right to amend or modify these terms, with prior notice through App/Email.",
                    "In case of any dispute, jurisdiction shall be limited to area of Company's registered office.",
                    "This Agreement shall be governed by laws of India."
                  ].map((term, i) => (
                    <div key={i} className="buyshare-terms-numbered-item">
                      <span className="buyshare-terms-number">{i + 1}</span>
                      <span>{term}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="buyshare-terms-section green">
                <div className="buyshare-terms-section-header">
                  <div className="buyshare-terms-section-icon green">📱</div>
                  <div>
                    <div className="buyshare-terms-section-title">Digital Consent Clause</div>
                    <div className="buyshare-terms-section-subtitle">The OTP verification, along with Date, Time, IP Address, and Device ID recorded in App, shall be considered as valid electronic consent and legally enforceable evidence.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyShare;
